import { useMutation, useQuery } from '@apollo/client';
import { useNotify } from '@newfront-insurance/next-notifications';
import { useCallback, useEffect, useMemo } from 'react';

import { GET_CUSTOM_SKILL_SHARE_RECIPIENTS, type CustomSkillRecord } from '../../../../../queries/chats';
import { SHARE_CUSTOM_SKILL_WITH_USER } from '../../../../../queries/mutations';
import { getErrorMessage } from '../../../lib/error-message';
import { getEmployeesByUserUuid } from '../../settings-people/employee-utils';
import type { SettingsEmployee } from '../../settings-people/employee-utils';
import { getRootCustomSkillUuid } from '../custom-skill-model';

export function useCustomSkillShares(skill: CustomSkillRecord) {
  const notify = useNotify();
  const rootSkillUuid = getRootCustomSkillUuid(skill);
  const {
    data,
    loading: areSharesLoading,
    error,
    refetch,
  } = useQuery(GET_CUSTOM_SKILL_SHARE_RECIPIENTS, {
    variables: { filters: { customSkillUuids: [rootSkillUuid] } },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });
  const shares = useMemo(() => data?.customSkillShares ?? [], [data?.customSkillShares]);
  const sharedEmployeesByUserUuid = useMemo(
    () =>
      getEmployeesByUserUuid(
        shares.flatMap(({ userUuid, user }) =>
          user
            ? [
                {
                  userUuid,
                  email: user.email,
                  name: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email,
                },
              ]
            : [],
        ),
      ),
    [shares],
  );

  useEffect(() => {
    if (error) {
      notify.error(getErrorMessage(error, 'Unable to load skill sharing.'));
    }
  }, [error, notify]);

  const loadShares = useCallback(async () => {
    await refetch({ filters: { customSkillUuids: [rootSkillUuid] } });
  }, [refetch, rootSkillUuid]);

  return {
    shares,
    sharedEmployeesByUserUuid,
    isLoading: areSharesLoading,
    reloadShares: loadShares,
  };
}

export function useAddCustomSkillShares({
  skill,
  reloadShares,
  onChanged,
}: {
  skill: CustomSkillRecord;
  reloadShares: () => Promise<void>;
  onChanged: () => Promise<void>;
}) {
  const notify = useNotify();
  const [shareCustomSkillWithUser] = useMutation(SHARE_CUSTOM_SKILL_WITH_USER);
  const rootSkillUuid = getRootCustomSkillUuid(skill);

  return useCallback(
    async (selectedEmployees: SettingsEmployee[]) => {
      try {
        await Promise.all(
          selectedEmployees.map((employee) =>
            shareCustomSkillWithUser({
              variables: {
                uuid: rootSkillUuid,
                command: { userUuid: employee.userUuid, permission: 'VIEWER' },
              },
            }),
          ),
        );
        await reloadShares();
        await onChanged();
        notify.success('Custom skill shared.');
        return true;
      } catch (shareError) {
        notify.error(getErrorMessage(shareError, 'Unable to share custom skill.'));
        return false;
      }
    },
    [notify, onChanged, reloadShares, rootSkillUuid, shareCustomSkillWithUser],
  );
}
